import { decode } from '@googlemaps/polyline-codec';
import { useMoment } from '@gorila-shared-ui/components';
import { useEffect } from 'react';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { getStreamingTimeline, getStreamingTimelineAvailability } from '../../../../services/streamingService';
import {
  capturingCurrentStateState,
  capturingState,
  capturingTimelineAvailabilityState,
  capturingTimelineEventState,
  capturingTimelineManualDateState,
  capturingTimelineMapEventState,
  capturingTimelineState,
  loadingCapturingTimelineState,
} from '../../../../storage/streaming';
import { StreamingTimeline, TimelineRecord } from '../../../../types/streaming';
import { getEndDateFromDate, getStartDateFromDate } from '../../../../utils/dates';

function CapturingTimelineProvider() {
  const capturingData = useRecoilValue(capturingState);
  const resetCapturingData = useResetRecoilState(capturingState);
  const { selectedDate: date } = capturingData;
  const currentState = useRecoilValue(capturingCurrentStateState);
  const setTimeline = useSetRecoilState(capturingTimelineState);
  const setTimelineAvailability = useSetRecoilState(capturingTimelineAvailabilityState);
  const resetTimeline = useResetRecoilState(capturingTimelineState);
  const resetAvailability = useResetRecoilState(capturingTimelineAvailabilityState);
  const setLoading = useSetRecoilState(loadingCapturingTimelineState);
  const resetEvent = useResetRecoilState(capturingTimelineEventState);
  const resetMapEvent = useResetRecoilState(capturingTimelineMapEventState);
  const resetManualDate = useResetRecoilState(capturingTimelineManualDateState);
  const { formatTimeFromTs } = useMoment();

  const startLoading = () => {
    setLoading(true);
  };

  const stopLoading = () => {
    setLoading(false);
  };

  const updateTimeline = (records: StreamingTimeline) => {
    const timeline = records.reverse().map((record): TimelineRecord => {
      return {
        ...record,
        startLabel: `${formatTimeFromTs(record.startTime)} ${
          record.startReference ? ' - ' + record.startReference : ''
        }`,
        endLabel: `${
          record.endTime
            ? `${formatTimeFromTs(record.endTime)} ${record.endReference ? ' - ' + record.endReference : ''}`
            : '-'
        }`,
        lineStringCoordinates: record.encodedPolyline
          ? decode(record.encodedPolyline).map((point) => {
              return [point[1], point[0]];
            })
          : undefined,
        events: record.events?.reverse(),
      };
    });
    setTimeline(timeline);
  };

  useEffect(() => {
    const loadTimeline = async () => {
      startLoading();
      if (!currentState?.asset._id || !currentState?.asset.client?._id || !currentState.asset.subClient?._id) {
        stopLoading();
        return;
      }
      const { data, error } = await getStreamingTimeline(
        currentState.asset._id,
        currentState.asset.client._id,
        currentState.asset.subClient._id,
        getStartDateFromDate(new Date(date)),
        getEndDateFromDate(new Date(date)),
      );
      if (data && !error) {
        updateTimeline(data);
      } else {
        setTimeline(undefined);
      }
      void getStreamingTimelineAvailability(
        currentState.asset._id,
        currentState.asset.client._id,
        currentState.asset.subClient._id,
        getStartDateFromDate(new Date(date)),
        getEndDateFromDate(new Date(date)),
      ).then(({ data: availabilityData, error: availabilityError }) => {
        if (availabilityData && !availabilityError) {
          setTimelineAvailability(availabilityData);
        } else {
          setTimelineAvailability(undefined);
        }
      });

      stopLoading();
    };
    loadTimeline();
  }, [currentState?.asset._id, currentState?.asset.client?._id, currentState?.asset.subClient?._id, date]);

  useEffect(() => {
    return () => {
      resetTimeline();
      resetAvailability();
      resetCapturingData();
      resetEvent();
      resetMapEvent();
      resetManualDate();
    };
  }, []);

  return null;
}

export default CapturingTimelineProvider;
